import React, { CSSProperties } from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { mdiSparkles, mdiMicrophone, mdiSortVariant, mdiMenu } from '@lumapps/lumx/icons';
import { Emphasis, ColorPalette, Theme } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import { SUGGESTED_FORMALITIES, SUGGESTED_TONES } from '../../constants';
import { UseRefineContentOutput } from '../../hooks/useRefineContentDialog';
import { WREX_GENERATED_CONTENT } from '../../keys';
import { ButtonPosition } from '../../types';

import '../index.scss';

const CLASSNAME = 'generative-content';

export interface UpdateContentMenuProps {
    theme: Theme;
    buttonPosition?: ButtonPosition;
    buttonRef: React.MutableRefObject<null>;
    selectedText?: string;
    onRefineContent: UseRefineContentOutput['onRefineContent'];
}

export const UpdateContentMenu = ({
    theme,
    buttonPosition,
    buttonRef,
    selectedText,
    onRefineContent,
}: UpdateContentMenuProps) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes('poc-ai-adapt-selection');

    return (
        <div className={`${CLASSNAME}__floating-button`} style={buttonPosition as CSSProperties}>
            <Menu.Trigger>
                <Menu.TriggerButton
                    leftIcon={mdiSparkles}
                    theme={theme}
                    emphasis={Emphasis.low}
                    hasBackground
                    ref={buttonRef}
                    color={ColorPalette.primary}
                    {...get({ element: 'ask-ai' })}
                >
                    {translateKey(WREX_GENERATED_CONTENT.ASK_AI)}
                </Menu.TriggerButton>

                <Menu>
                    <Menu.Section label={translateKey(WREX_GENERATED_CONTENT.MODIFY_SELECTION_SECTION)}>
                        <Menu.Item
                            onClick={() => onRefineContent(WREX_GENERATED_CONTENT.PROMPT_REFINE_STYLE, selectedText)}
                            icon={mdiSparkles}
                            {...get({ element: 'refine-style' })}
                        >
                            {translateKey(WREX_GENERATED_CONTENT.MENU_REFINE_STYLE)}
                        </Menu.Item>

                        <Menu.Submenu
                            icon={mdiMicrophone}
                            label={translateKey(WREX_GENERATED_CONTENT.MENU_REFINE_TONE)}
                        >
                            <Menu.Section label={translateKey(WREX_GENERATED_CONTENT.FORMALITY)}>
                                {Object.entries(SUGGESTED_FORMALITIES).map(([key, { resume, dataId }]) => (
                                    <Menu.Item
                                        key={key}
                                        onClick={() =>
                                            onRefineContent(
                                                WREX_GENERATED_CONTENT.PROMPT_REFINE_TONE,
                                                selectedText,
                                                resume,
                                            )
                                        }
                                        {...get({ element: `formality-${dataId}` })}
                                    >
                                        {translateKey(resume)}
                                    </Menu.Item>
                                ))}
                            </Menu.Section>

                            <Menu.Section label={translateKey(WREX_GENERATED_CONTENT.TONE)}>
                                {Object.entries(SUGGESTED_TONES).map(([key, { resume, dataId }]) => (
                                    <Menu.Item
                                        onClick={() =>
                                            onRefineContent(
                                                WREX_GENERATED_CONTENT.PROMPT_REFINE_TONE,
                                                selectedText,
                                                resume,
                                            )
                                        }
                                        key={key}
                                        {...get({ element: `tov-${dataId}` })}
                                    >
                                        {translateKey(resume)}
                                    </Menu.Item>
                                ))}
                            </Menu.Section>
                        </Menu.Submenu>
                        <Menu.Item
                            icon={mdiSortVariant}
                            onClick={() => onRefineContent(WREX_GENERATED_CONTENT.PROMPT_REFINE_SHORTEN, selectedText)}
                            {...get({ element: 'shorten' })}
                        >
                            {translateKey(WREX_GENERATED_CONTENT.MENU_REFINE_SHORTEN)}
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => onRefineContent(WREX_GENERATED_CONTENT.PROMPT_REFINE_LENGTHEN, selectedText)}
                            icon={mdiMenu}
                            {...get({ element: 'lengthen' })}
                        >
                            {translateKey(WREX_GENERATED_CONTENT.MENU_REFINE_LENGTHEN)}
                        </Menu.Item>
                    </Menu.Section>
                </Menu>
            </Menu.Trigger>
        </div>
    );
};
